import { get_token } from '@/router/index.js';
import { build_global_search_request } from './navbar_safe_url_helpers';

export const navbar_search_branding_methods = {
  open_search_modal_from_nav() {
    this.open_search_modal();
    this.$nextTick(() => {
      const modal_el = this.$refs.global_search_modal;
      const modal_input = modal_el?.querySelector('input[type="search"]');
      modal_input?.focus();
      modal_input?.select();
    });
  },

  on_search_input() {
    this.show_panel = true;
    clearTimeout(this.debounce_id);
    const q = (this.search_query || '').trim();
    if (!q) {
      this.results = [];
      this.total_count = 0;
      return;
    }
    this.debounce_id = setTimeout(() => this.run_search(q, 0), 250);
  },

  async run_search(query, offset = 0, expand = false) {
    const token = get_token();
    if (!token) {
      this.$router.push({ name: 'Login' });
      return;
    }

    this.is_searching = true;
    try {
      const search_request = this.build_global_search_request(query, offset, expand, token);
      const res = await search_request();
      await this.apply_search_response(res);
    } catch (e) {
      this.$notyf?.info?.(e?.message || 'Search failed');
      this.results = [];
      this.total_count = 0;
    } finally {
      this.is_searching = false;
    }
  },

  build_global_search_request(query, offset, expand, token) {
    return build_global_search_request({
      query,
      offset,
      expand,
      token,
      include: this.is_admin() ? 'user,profile,company' : 'profile,company',
      limit: this.limit,
    });
  },

  async apply_search_response(res) {
    const data = await this.parse_search_response(res);

    this.throw_if_search_failed(res, data);
    this.results = this.extract_search_results(data);
    this.total_count = this.extract_search_total_count(data);
  },

  parse_search_response(res) {
    return res.json().catch(() => ({}));
  },

  throw_if_search_failed(res, data) {
    if (!res.ok) {
      throw new Error(data?.message || 'Search failed');
    }
  },

  extract_search_results(data) {
    return Array.isArray(data?.results) ? data.results : [];
  },

  extract_search_total_count(data) {
    return Number.isFinite(data?.count) ? data.count : 0;
  },


  async checkAndPushRoute(loc) {
    const resolved = this.resolve_search_route(loc);
    if (!resolved?.matched?.length) return false;
    if (this.$route.fullPath !== resolved.fullPath) {
      await this.$router.push(resolved);
    }
    return true;
  },

  resolve_search_route(loc) {
    const route_resolver = this.get_search_route_resolver(loc);
    return route_resolver ? route_resolver() : null;
  },

  get_search_route_resolver(loc) {
    if (typeof loc === 'string') {
      return () => this.$router.resolve(loc);
    }
    if (loc?.name) {
      return () => this.resolve_named_search_route(loc);
    }
    if (this.is_search_route_object(loc)) {
      return () => this.$router.resolve(loc);
    }
    return null;
  },

  resolve_named_search_route(loc) {
    return this.$router.hasRoute?.(loc.name) ? this.$router.resolve(loc) : null;
  },

  is_search_route_object(loc) {
    return typeof loc === 'object' && loc !== null;
  },

  async open_result(item) {
    const type = this.get_search_result_type(item);
    const id = this.get_search_result_id(item);

    if (type === 'user') {
      await this.open_user_search_result(id);
      return;
    }

    await this.open_non_user_search_result(type, id);
  },

  get_search_result_type(item) {
    return String(item?.type || '').toLowerCase();
  },

  get_search_result_id(item) {
    return String(item?.id ?? '');
  },

  async open_non_user_search_result(type, id) {
    const route_config = this.search_result_routes(type, id);

    if (!route_config) {
      this.$notyf?.info?.('No handler for this result type yet.');
      return;
    }

    await this.open_search_result_routes(route_config);
  },

  async open_search_result_routes(route_config) {
    const opened = await this.try_push_search_routes(route_config.routes);

    if (!opened) {
      this.$notyf?.info?.(route_config.error);
    }
  },

  async open_user_search_result(id) {
    if (!this.is_admin()) {
      this.$notyf?.info?.('You do not have permission to view user records.');
      return;
    }

    const ok = await this.try_push_search_routes([
      { name: 'Settings' },
      `/users/${id}`,
      `/user/${id}`,
      { path: `/settings?focus=user&user_id=${id}` },
    ]);
    if (!ok) this.$notyf?.info?.('No user route found.');
  },

  search_result_routes(type, id) {
    const routes = {
      profile: {
        routes: [{ name: 'Profile' }, `/profiles/${id}`, `/profile/${id}`],
        error: 'No profile route found.',
      },
      company: {
        routes: [{ name: 'Profile' }, `/companies/${id}`, `/company/${id}`],
        error: 'No company route found.',
      },
      audit: {
        routes: [{ name: 'AuditTrail' }, `/audits?focus_id=${encodeURIComponent(id)}`],
        error: 'No audit route found.',
      },
    };
    return Reflect.get(routes, type);
  },

  async try_push_search_routes(candidates) {
    for (const candidate of candidates) {
      if (await this.checkAndPushRoute(candidate)) {
        this.close_search_ui();
        return true;
      }
    }
    return false;
  },

  close_search_ui() {
    this.show_panel = false;
    this.close_search_modal?.();
  },

  open_search_modal() {
    const el = this.$refs.global_search_modal;
    if (!el) return;
    // eslint-disable-next-line no-undef
    this.search_modal = this.search_modal || new bootstrap.Modal(el, { backdrop: true, keyboard: true });
    this.search_modal.show();
    this._search_modal_visible = true;
    this.$nextTick(() => this.focus_search_modal_input(el));
  },

  focus_search_modal_input(el) {
    const input = el.querySelector('input[type="search"]');
    input?.focus();
    if (typeof input?.selectionStart === 'number') {
      const len = input.value.length;
      input.setSelectionRange(len, len);
    }
  },

  close_search_modal() {
    this.search_modal?.hide?.();
    this._search_modal_visible = false;
  },

  branding_logo(data) {
    return Reflect.get(Reflect.get(data ?? {}, 'logos') ?? {}, 'logo');
  },

  branding_organization_id() {
    return Reflect.get(this.user ?? {}, 'company_id')
      ?? Reflect.get(this.user ?? {}, 'organization_id');
  },

  apply_branding_logo(data) {
    const logo = this.branding_logo(data);
    if (logo) {
      this.logo_url = logo;
    }
  },

  async init_branding() {
    if (!this.$branding) return;
    const branding_data = this.$branding.get_branding();
    this.apply_branding_logo(branding_data);
    if (branding_data.initialized) return;
    const org_id = this.branding_organization_id();
    if (!org_id) return;
    const loaded = await this.$branding.load_branding(org_id);
    this.apply_branding_logo(loaded);
  },

  get_navbar_logo_src() {
    return this.logo_url || this.styleConfig.logo;
  },
  handle_navbar_logo_error(e) {
    e.target.src = this.styleConfig.logo;
  },
  page_next() {
    if (this.offset + this.limit < this.total_count) {
      this.run_search(this.search_query, this.offset + this.limit);
    }
  },
  page_prev() {
    if (this.offset > 0) {
      this.run_search(this.search_query, Math.max(0, this.offset - this.limit));
    }
  },
  on_nav_input(e) {
    if (!this._search_modal_visible) {
      this.open_search_modal_from_nav();
    }
    this.search_query = (e?.target?.value || '').trimStart();
    this.on_search_input();
  },
};